import React, { useState, useEffect } from 'react';
import axios from '../../api/axios';
import Layout from '../../layout/layout';
import {
  Box,
  TextField,
  Typography,
  Divider,
  Button,
  Grid,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Chip,
  Autocomplete,
  Avatar
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CancelIcon from '@mui/icons-material/Cancel';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import PauseIcon from '@mui/icons-material/Pause';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import SubscriptionDetailsDialog from './components/SubscriptionDetailsDialog';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import EditDislikesDialog from './components/EditDislikesDialog';
import EditAllergiesDialog from './components/EditAllergiesDialog';
import ManageAccountDialog from './components/ManageAccountDialog';
import EditDriverDialog from './components/EditDriverDialog';
import PauseListDialog from './components/PauseListDialog';
// import UpdateAddressDialog from './components/UpdateAddressDialog';
import AddressesManagementDialog from './components/AddressesManagementDialog';
import AddressSchedulerDialog from './components/AddressSchedulerDialog';

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [openRenewDialog, setOpenRenewDialog] = useState(false);
  const [subscriptionDetailsOpen, setSubscriptionDetailsOpen] = useState(false);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState(null);
  const [editDislikesOpen, setEditDislikesOpen] = useState(false);
  const [editAllergiesOpen, setEditAllergiesOpen] = useState(false);
  const [manageAccountOpen, setManageAccountOpen] = useState(false);
  const [editDriverOpen, setEditDriverOpen] = useState(false);
  const [pauseListOpen, setPauseListOpen] = useState(false);
  // const [updateAddressOpen, setUpdateAddressOpen] = useState(false);
  const [addressesDialogOpen, setAddressesDialogOpen] = useState(false);
  const [schedulerDialogOpen, setSchedulerDialogOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const abortControllerRef = React.useRef(null);

  const handleSearch = async (searchValue) => {
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (searchValue.length > 2) {
      setLoading(true);
      abortControllerRef.current = new AbortController();
      try {
        const response = await axios.get(`user/search/?q=${searchValue}`, {
          signal: abortControllerRef.current.signal
        });
        setUsers(response.data.users);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching users:', error);
          setUsers([]);
        }
      } finally {
        setLoading(false);
      }
    } else {
      setUsers([]);
    }
  };

  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
    handleSearch(newInputValue);
  };

  const handleUserSelect = (event, user) => {
    if (user) {
      setSelectedUser(user);
      setInputValue(user.full_name);
    }
  };

  const navigate = useNavigate();

  // Cleanup pending requests on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Fetch user details when selectedUser changes
  useEffect(() => {
    if (selectedUser) {
      const fetchUserDetails = async () => {
        try {
          const response = await axios.get(`user/info/?user_id=${selectedUser.id}`);
          setUserDetails(response.data);
        } catch (error) {
          console.error('Error fetching user details:', error);
        }
      };
      fetchUserDetails();
    }
  }, [selectedUser]);

  const handleRenewClick = () => {
    setOpenRenewDialog(true);
  };

  const handleRenewConfirm = () => {
    setOpenRenewDialog(false);
    navigate(`/renew-subscription?userId=${userDetails.id}`);
  };

  const handleEditDislikesClick = () => {
    setEditDislikesOpen(true);
  };

  const handleEditAllergiesClick = () => {
    setEditAllergiesOpen(true);
  };

  const handleManageAccountClick = () => {
    setManageAccountOpen(true);
  };

  const handleEditDriverClick = () => {
    setEditDriverOpen(true);
  };

  const handlePauseListClick = () => {
    setPauseListOpen(true);
  };

  // const handleUpdateAddressClick = () => {
  //   setUpdateAddressOpen(true);
  // };
  const handleAddressesDialogOpen = () => {
    setAddressesDialogOpen(true);
  };
  const handleAddressesDialogClose = () => {
    setAddressesDialogOpen(false);
  };
  const handleSchedulerDialogOpen = () => {
    setSchedulerDialogOpen(true);
  };
  const handleSchedulerDialogClose = () => {
    setSchedulerDialogOpen(false);
  };

  const handleSubscriptionRowClick = (subscriptionId) => {
    setSelectedSubscriptionId(subscriptionId);
    setSubscriptionDetailsOpen(true);
  };

  const handleRefreshUserDetails = async () => {
    if (selectedUser) {
      setRefreshing(true);
      try {
        const response = await axios.get(`user/info/?user_id=${selectedUser.id}`);
        setUserDetails(response.data);
      } catch (error) {
        console.error('Error refreshing user details:', error);
      } finally {
        setRefreshing(false);
      }
    }
  };

  const handleDriverUpdated = (newDriverId) => {
    // Update the userDetails state with the new driver ID
    setUserDetails((prev) => ({ ...prev, driver: newDriverId }));
  };

  const handleDislikesSave = (updatedDislikes) => {
    // Save the updated dislikes (you can add an API call here)
    setUserDetails((prev) => ({ ...prev, dislikes: updatedDislikes }));
    setEditDislikesOpen(false);
  };

  const handleAllergiesSave = (updatedAllergies) => {
    setUserDetails((prev) => ({ ...prev, allergies: updatedAllergies }));
    setEditAllergiesOpen(false);
  };

  // Removed handleAddressUpdated (was used by UpdateAddressDialog)

  const userDetailsSection = (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        padding: { xs: 1, md: 2 },
      }}
    >
      {userDetails ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6">{userDetails.name}</Typography>
              <IconButton
                size="small"
                onClick={handleRefreshUserDetails}
                disabled={refreshing}
                title="Refresh user details"
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {userDetails.suspended ? (
                  <>
                    <CancelIcon sx={{ color: 'error.main', fontSize: 20 }} titleAccess="Account Suspended" />
                    <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 'medium' }}>
                      Suspended
                    </Typography>
                  </>
                ) : (
                  <>
                    <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} titleAccess="Account Active" />
                    <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 'medium' }}>
                      Active
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<ShoppingCartIcon />}
              onClick={() => navigate(`/buy-subscription?userId=${userDetails.id}`)}
              size="small"
            >
              Buy
            </Button>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={1}>
            {/* Personal Information */}
            <Grid item xs={12} md={6} lg={4}>
              <Paper elevation={1} sx={{ p: 1.5 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>Personal</Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>ID:</strong> {userDetails.id}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Name:</strong> {userDetails.name}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Email:</strong> {userDetails.email}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Phone:</strong> {userDetails.phone}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleManageAccountClick}
                  fullWidth
                >
                  Manage Account
                </Button>
              </Paper>
            </Grid>

            {/* Demographics */}
            <Grid item xs={12} md={6} lg={4}>
              <Paper elevation={1} sx={{ p: 1.5 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>Demographics</Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Age:</strong> {userDetails.age}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Gender:</strong> {userDetails.gender}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Birthday:</strong> {userDetails.birthday}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Area:</strong> {userDetails.area}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2">
                    <strong>Driver:</strong> {userDetails.driver}
                  </Typography>
                  <IconButton size="small" onClick={handleEditDriverClick}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleAddressesDialogOpen}
                  fullWidth
                  sx={{ mt: 1 }}
                >
                  Addresses
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleSchedulerDialogOpen}
                  fullWidth
                  sx={{ mt: 1 }}
                >
                  Address Scheduler
                </Button>
              </Paper>
            </Grid>

            {/* Subscription Details */}
            <Grid item xs={12} md={6} lg={4}>
              <Paper elevation={1} sx={{ p: 1.5 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>Subscription</Typography>

                <Grid container spacing={1} sx={{ mb: 1 }}>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Package:</strong> {userDetails.package}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Days Left:</strong> {userDetails.remaining_days}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Remaining:</strong> {userDetails.remaining_subscriptions}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>End Date:</strong> {userDetails.end_date}
                    </Typography>
                  </Grid>
                </Grid>

                {/* Show running subscription's double box days */}
                {userDetails.subscriptions?.find(sub => sub.status === 'Running')?.double_box_days && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                    <Typography variant="body2">
                      <strong>Double Box Days:</strong>
                    </Typography>
                    {userDetails.subscriptions.find(sub => sub.status === 'Running').double_box_days.map((day, index) => (
                      <Chip
                        key={index}
                        label={day}
                        size="small"
                        sx={{
                          backgroundColor: '#4CAF50',
                          color: '#FFF',
                          fontSize: '0.65rem',
                          height: '18px',
                          ml: 0.5
                        }}
                      />
                    ))}
                  </Box>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<PauseIcon />}
                    onClick={handlePauseListClick}
                    size="small"
                    fullWidth
                    sx={{
                      backgroundColor: '#FFB300',
                      color: '#000',
                      '&:hover': {
                        backgroundColor: '#FF8F00'
                      }
                    }}
                  >
                    Pause List
                  </Button>

                  {userDetails.subscriptions?.some(sub => sub.status === 'Running') && (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AutorenewIcon />}
                      onClick={handleRenewClick}
                      size="small"
                      fullWidth
                    >
                      Renew
                    </Button>
                  )}
                </Box>

                {userDetails.subscriptions?.some(sub => sub.status === 'Running') && (
                  <SubscriptionDetailsDialog
                    open={openRenewDialog}
                    onClose={() => setOpenRenewDialog(false)}
                    onConfirm={handleRenewConfirm}
                    subscriptionId={userDetails.subscriptions.find(sub => sub.status === 'Running').id}
                    title="Renew Subscription"
                    confirmText="Confirm Renewal"
                    message="Are you sure you want to renew this subscription?"
                  />
                )}
              </Paper>
            </Grid>

            {/* Allergies Section */}
            <Grid item xs={12} md={6} lg={4}>
              <Paper elevation={1} sx={{ p: 1.5 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>Allergies</Typography>

                {userDetails.allergies && userDetails.allergies.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                    {userDetails.allergies.map((allergy, index) => (
                      <Chip
                        key={index}
                        label={allergy}
                        size="small"
                        sx={{
                          backgroundColor: '#B71C1C',
                          color: '#FFF',
                          '&:hover': {
                            backgroundColor: '#7F0000'
                          }
                        }}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                    No allergies selected
                  </Typography>
                )}

                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleEditAllergiesClick}
                  fullWidth
                  startIcon={<EditIcon />}
                >
                  Edit Allergies
                </Button>
              </Paper>
            </Grid>

            {/* Dislikes Section */}
            <Grid item xs={12} md={6} lg={4}>
              <Paper elevation={1} sx={{ p: 1.5 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>Dislikes</Typography>

                {userDetails.dislikes && userDetails.dislikes.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                    {userDetails.dislikes.map((dislike, index) => (
                      <Chip
                        key={index}
                        label={dislike}
                        size="small"
                        sx={{
                          backgroundColor: '#D84315',
                          color: '#FFF',
                          '&:hover': {
                            backgroundColor: '#BF360C'
                          }
                        }}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                    No dislikes selected
                  </Typography>
                )}

                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleEditDislikesClick}
                  fullWidth
                  startIcon={<EditIcon />}
                >
                  Edit Dislikes
                </Button>
              </Paper>
            </Grid>
          </Grid>

          <EditDislikesDialog
            key={userDetails.id} // Add this line!
            open={editDislikesOpen}
            onClose={() => setEditDislikesOpen(false)}
            onSave={handleDislikesSave}
            initialDislikes={userDetails.dislikes || []}
            userId={userDetails.id}
          />

          <EditAllergiesDialog
            key={`allergies-${userDetails.id}`} // remount on user change so props reseed
            open={editAllergiesOpen}
            onClose={() => setEditAllergiesOpen(false)}
            onSave={handleAllergiesSave}
            initialAllergies={userDetails.allergies || []}
            userId={userDetails.id}
          />

          <SubscriptionDetailsDialog
            open={subscriptionDetailsOpen}
            onClose={() => setSubscriptionDetailsOpen(false)}
            subscriptionId={selectedSubscriptionId}
            title="Subscription Details"
          />

          <ManageAccountDialog
            open={manageAccountOpen}
            onClose={() => setManageAccountOpen(false)}
            userId={userDetails.id}
            userName={userDetails.name}
          />

          <EditDriverDialog
            open={editDriverOpen}
            onClose={() => setEditDriverOpen(false)}
            userId={userDetails.id}
            currentDriverId={userDetails.driver}
            onDriverUpdated={handleDriverUpdated}
          />

          <PauseListDialog
            open={pauseListOpen}
            onClose={() => setPauseListOpen(false)}
            userId={userDetails.id}
            userName={userDetails.name}
          />

          {/** Removed single UpdateAddressDialog in favor of AddressesManagementDialog */}

          <AddressesManagementDialog
            open={addressesDialogOpen}
            onClose={handleAddressesDialogClose}
            userId={userDetails.id}
            userName={userDetails.name}
            userDefaults={{
              receiver_full_name: userDetails.name || '',
              phone_number: userDetails.phone || userDetails.phone_number || '',
              area_name: userDetails.area || '',
              name: userDetails.address?.name || '',
              block: userDetails.address?.block || '',
              street: userDetails.address?.street || '',
              jaddah: userDetails.address?.jaddah || '',
              house: userDetails.address?.house || '',
              floor: userDetails.address?.floor || '',
              apartment: userDetails.address?.apartment || '',
              extra_direction: userDetails.address?.extra_direction || '',
              inner_note: userDetails.address?.inner_note || '',
              assigned_driver: userDetails.address?.assigned_driver || ''
            }}
          />

          <AddressSchedulerDialog
            open={schedulerDialogOpen}
            onClose={handleSchedulerDialogClose}
            userId={userDetails.id}
            userName={userDetails.name}
          />

          {/* Display total number of subscriptions */}
          <Typography variant="body2" sx={{ mt: 2, fontWeight: 'bold' }}>
            Total Subscriptions: {userDetails.subscriptions ? userDetails.subscriptions.length : 0}
          </Typography>

          {/* Subscriptions Table */}
          {userDetails.subscriptions && userDetails.subscriptions.length > 0 ? (
            <>
              <Typography variant="subtitle1" sx={{ mt: 1, mb: 1, fontWeight: 'bold' }}>Subscriptions</Typography>
              <TableContainer
                component={Paper}
                sx={{
                  maxHeight: 500,
                  overflow: 'auto'
                }}
              >
                <Table stickyHeader aria-label="subscriptions table" size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '0.75rem' }}>Start</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>End</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>Duration</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>Remaining</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>Package</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>Double Box</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userDetails.subscriptions.map((subscription, index) => (
                      <TableRow
                        key={index}
                        onClick={() => handleSubscriptionRowClick(subscription.id)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                      >
                        <TableCell sx={{ fontSize: '0.75rem' }}>{subscription.start_date}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>{subscription.end_date || 'N/A'}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>{subscription.duration}d</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>{subscription.remaining_days}d</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>{subscription.package}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>
                          {subscription.double_box_days && subscription.double_box_days.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {subscription.double_box_days.map((day, dayIndex) => (
                                <Chip
                                  key={dayIndex}
                                  label={day}
                                  size="small"
                                  sx={{
                                    backgroundColor: '#4CAF50',
                                    color: '#FFF',
                                    fontSize: '0.65rem',
                                    height: '20px'
                                  }}
                                />
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              None
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {subscription.status === 'Pending' && (
                              <HourglassEmptyIcon sx={{ fontSize: 16 }} color="secondary" />
                            )}
                            {subscription.status === 'Running' && (
                              <CheckCircleIcon sx={{ fontSize: 16 }} color="primary" />
                            )}
                            {subscription.status === 'Expired' && (
                              <CancelIcon sx={{ fontSize: 16 }} color="error" />
                            )}
                            <Typography variant="caption" sx={{ ml: 0.5 }}>
                              {subscription.status}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Typography variant="body2" sx={{ mt: 2 }}>
              No subscriptions found.
            </Typography>
          )}
        </>
      ) : (
        <Typography variant="h6">Select a user to view details</Typography>
      )}
    </Box>
  );

  const body = (
    <Box sx={{ height: 'calc(100vh - 64px)' }}>
      {/* Centered Auto-suggest Search Bar */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Autocomplete
          options={users}
          getOptionLabel={(option) => option.full_name || ''}
          value={selectedUser}
          onChange={handleUserSelect}
          onInputChange={handleInputChange}
          inputValue={inputValue}
          loading={loading}
          filterOptions={(x) => x} // Don't filter, we handle filtering on server
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search by name, ID, phone number, or email"
              variant="outlined"
              sx={{ width: { xs: '100%', sm: '500px', md: '600px' } }}
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', p: 1 }}>
              <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 32, height: 32, fontSize: '0.8rem' }}>
                {option.full_name?.charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {option.full_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ID: {option.id} • Phone: {option.phone_number}
                </Typography>
                {option.remaining_subscriptions > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                    <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} />
                    <Typography variant="caption" color="success.main">
                      {option.remaining_subscriptions} subscription(s) remaining
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
          noOptionsText={inputValue.length <= 2 ? "Type at least 3 characters to search" : "No users found"}
          sx={{ width: { xs: '100%', sm: '500px', md: '600px' } }}
        />
      </Box>

      {/* User Details - Takes full width */}
      <Box sx={{ height: 'calc(100% - 80px)', p: 2 }}>
        {userDetailsSection}
      </Box>
    </Box>
  );

  return <Layout body={body} title="User Management" />;
}

export default UserManagement;